import { useCallback, useEffect, useReducer, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ONE_MINUTE_MS } from 'pure-js/libs/Consts'
import { isAIDebriefActivity } from 'pure-js/libs/isAIDebriefActivity'
import { useGetUpcomingActivityForGamer } from '../../apiLayer/queryHooks/queries'
import { isAIDebriefPagePath } from '../../config/routes/routeConfig'
import type { ActivityWithSession } from '../../types/GeneralTypes'
import { useGlobalWhereby } from './useGlobalWhereby'

/** How long a started debrief gathers its team before someone in the call elsewhere is shown the way to it. */
export const DEBRIEF_STARTED_NOTICE_DELAY_MS = ONE_MINUTE_MS // 1 min

// Renders again once the moment arrives, so a comparison with the clock made while rendering turns on time.
const useHasMomentPassed = (momentMs: number | undefined): boolean => {
  const [, renderAgain] = useReducer((renderCount: number) => renderCount + 1, 0)

  useEffect(() => {
    if (momentMs === undefined) return undefined
    const waitMs = momentMs - Date.now()
    if (waitMs <= 0) return undefined
    const timer = setTimeout(renderAgain, waitMs)
    return () => clearTimeout(timer)
  }, [momentMs])

  return momentMs !== undefined && Date.now() >= momentMs
}

const resolveNoticeDueAtMs = (debrief: ActivityWithSession | undefined): number | undefined => {
  const startMs = debrief ? new Date(debrief.startDate).getTime() : Number.NaN
  return Number.isFinite(startMs) ? startMs + DEBRIEF_STARTED_NOTICE_DELAY_MS : undefined
}

/** Points someone in the team call to the AI debrief page once their debrief has been running for a minute. */
export const useDebriefStartedNotice = (isParticipant: boolean) => {
  const { pathname } = useLocation()
  const { isCallActive } = useGlobalWhereby()
  const { data: response } = useGetUpcomingActivityForGamer({ enabled: isParticipant })
  const [dismissedDebriefId, setDismissedDebriefId] = useState<string | null>(null)

  const activity = response?.data ?? undefined
  const debrief = isAIDebriefActivity(activity?.type) ? activity : undefined
  const hasDebriefRunLongEnough = useHasMomentPassed(resolveNoticeDueAtMs(debrief))
  const isDismissed = !!debrief && debrief.id === dismissedDebriefId
  const isOnDebriefPage = isAIDebriefPagePath(pathname)
  const isAwayFromRunningDebrief = isCallActive && hasDebriefRunLongEnough && !isOnDebriefPage
  const isDebriefStartedNoticeVisible = isAwayFromRunningDebrief && !isDismissed

  const dismissDebriefStartedNotice = useCallback(() => setDismissedDebriefId(debrief?.id ?? null), [debrief?.id])

  return { isDebriefStartedNoticeVisible, dismissDebriefStartedNotice }
}
